import { formatDecimal } from '@shared/lib/format-decimal';
import { formatLedgerAmount } from '@shared/lib/format-ledger-amount';
import { formatAssetNetworkLabel, normalizeAssetBalances } from '@shared/lib/asset-balance';
import type { AssetBalanceDto, WalletMonitoringBalance } from '@shared/api/contracts';

export const WALLET_MONEY_DECIMALS = 4;

/** Format decimal string for display without converting the full value through Number. */
export const formatWalletDecimal = (
  value: string | null | undefined,
  options?: { asset?: string; emptyLabel?: string; decimals?: number },
): string => formatDecimal(value, options);

/** Plan/fact money — always 4 fractional digits. */
export const formatWalletMoney = (
  value: string | null | undefined,
  options?: { asset?: string; emptyLabel?: string },
): string =>
  formatDecimal(value, {
    ...options,
    decimals: WALLET_MONEY_DECIMALS,
    trimTrailingZeros: false,
  });

export const isZeroDecimal = (value: string | null | undefined): boolean => {
  if (value == null) return false;
  const trimmed = value.trim();
  if (!trimmed) return false;
  return /^-?0+(\.0+)?$/.test(trimmed);
};

export const formatWalletBalanceLines = (balances: WalletMonitoringBalance[]): string[] => {
  if (!balances.length) return [];

  return balances.map((balance) => {
    const amount = formatWalletMoney(balance.amount, { asset: balance.symbol });
    const symbol = balance.symbol.trim().toUpperCase();
    if (balance.amountUsd == null || symbol === 'USDT' || symbol === 'USDC' || symbol === 'USD') {
      return amount;
    }
    const usd = formatWalletMoney(balance.amountUsd, { asset: 'USD' });
    return `${amount} (${usd})`;
  });
};

export const formatWalletAssetBalanceLines = (
  balances: AssetBalanceDto[] | undefined,
  historicalLabel: string,
  emptyLabel = '—',
): string[] => {
  const rows = normalizeAssetBalances(balances);
  if (!rows.length) {
    return [];
  }

  return rows.map((balance) => {
    const amount = formatLedgerAmount(balance.amount, {
      assetLabel: balance.assetCode,
      emptyLabel,
    });
    const network = formatAssetNetworkLabel(balance.network, historicalLabel);
    return `${amount} · ${network}`;
  });
};
